import { useEffect, useState } from "react";
import { Loader2, Plus, Search, UserRound } from "lucide-react";

import { ResidentCreateDialog } from "@/components/trust_suite/ResidentCreateDialog";
import { ResidentDetail } from "@/components/trust_suite/ResidentDetail";
import { residentListMeta } from "@/components/trust_suite/residentDisplay";
import { SuiteTrackingLayout } from "@/components/workflow_tracking/SuiteTrackingLayout";
import { formatRelativeTime } from "@/components/module_summary/formatRelativeTime";
import { ModuleSummaryStrip } from "@/components/module_summary/ModuleSummaryStrip";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useMunicipality } from "@/context/MunicipalityContext";
import { useResidentTrack } from "@/hooks/useResidentTrack";
import type { TrustComplaintQueue } from "@/api/trust_complaints";
import { cn } from "@/lib/utils";

type ResidentDirectoryPanelProps = {
  onOpenComplaint?: (complaintId: number, queue: TrustComplaintQueue) => void;
  focusResidentId?: number | null;
  onFocusResidentConsumed?: () => void;
};

export function ResidentDirectoryPanel({
  onOpenComplaint,
  focusResidentId,
  onFocusResidentConsumed,
}: ResidentDirectoryPanelProps) {
  const { municipality, municipalities, isLoading: municipalitiesLoading } = useMunicipality();
  const municipalityId = municipality?.id;
  const track = useResidentTrack({ municipalityId });
  const [createOpen, setCreateOpen] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [addressLine1, setAddressLine1] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [postalCode, setPostalCode] = useState("");

  const resetCreateForm = () => {
    setDisplayName("");
    setEmail("");
    setPhone("");
    setAddressLine1("");
    setCity("");
    setState("");
    setPostalCode("");
  };

  const handleCreateResident = async () => {
    const addresses =
      addressLine1.trim().length > 0
        ? [
            {
              kind: "home" as const,
              address_line1: addressLine1.trim(),
              city: city.trim() || null,
              state: state.trim() || null,
              postal_code: postalCode.trim() || null,
              is_primary: true,
            },
          ]
        : undefined;

    await track.createResident({
      display_name: displayName.trim() || undefined,
      email: email.trim() || null,
      phone: phone.trim() || null,
      addresses,
    });

    setCreateOpen(false);
    resetCreateForm();
  };

  useEffect(() => {
    if (focusResidentId == null) return;
    track.setSelectedId(focusResidentId);
    onFocusResidentConsumed?.();
  }, [focusResidentId]);

  if (municipalitiesLoading || municipalities.length === 0 || !municipalityId) {
    return (
      <SuiteTrackingLayout
        municipalitiesLoading={municipalitiesLoading}
        municipalitiesCount={municipalities.length}
        municipalitySelected={Boolean(municipalityId)}
        emptyIcon={UserRound}
        selectMunicipalityMessage="Select a municipality above to view residents."
        summaryStrip={null}
        sidebar={null}
        detail={null}
      />
    );
  }

  return (
    <>
      <SuiteTrackingLayout
        testId="resident-directory-panel"
        municipalitiesLoading={false}
        municipalitiesCount={municipalities.length}
        municipalitySelected
        emptyIcon={UserRound}
        selectMunicipalityMessage="Select a municipality above to view residents."
        summaryStrip={
          <ModuleSummaryStrip
            className="border-b"
            stats={[{ label: "Residents", value: track.totalCount }]}
            maxStats={1}
            loading={track.isLoading}
          />
        }
        sidebar={
          <div className="flex min-h-0 flex-1 flex-col">
            <div className="space-y-3 border-b p-4">
              <div className="flex gap-2">
                <div className="relative min-w-0 flex-1">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    className="pl-9"
                    placeholder="Search residents…"
                    value={track.search}
                    onChange={(event) => track.setSearch(event.target.value)}
                    aria-label="Search residents"
                  />
                </div>
                <Button
                  type="button"
                  size="icon"
                  variant="outline"
                  aria-label="Add resident"
                  onClick={() => setCreateOpen(true)}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <ScrollArea className="min-h-0 flex-1">
              <div className="space-y-2 p-2">
                {track.isLoading && (
                  <div className="flex items-center justify-center py-8 text-muted-foreground">
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Loading residents…
                  </div>
                )}

                {track.error && (
                  <p className="px-2 text-sm text-destructive">Could not load residents.</p>
                )}

                {!track.isLoading &&
                  track.residents.map((resident) => (
                    <button
                      key={resident.id}
                      type="button"
                      className={cn(
                        "w-full rounded-lg border bg-background p-3 text-left transition-colors hover:bg-muted/40",
                        track.selectedId === resident.id &&
                          "border-primary/60 bg-primary/5 ring-1 ring-primary/20",
                      )}
                      onClick={() => track.setSelectedId(resident.id)}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <span className="line-clamp-2 min-w-0 flex-1 text-sm font-medium leading-snug">
                          {resident.display_name}
                        </span>
                        <span className="shrink-0 text-[10px] text-muted-foreground">#{resident.id}</span>
                      </div>
                      {residentListMeta(resident) ? (
                        <p className="mt-1 truncate text-[11px] text-muted-foreground">
                          {residentListMeta(resident)}
                        </p>
                      ) : null}
                      {resident.last_interaction_at ? (
                        <p className="mt-1 text-[11px] text-muted-foreground">
                          Active {formatRelativeTime(resident.last_interaction_at)}
                        </p>
                      ) : null}
                    </button>
                  ))}

                {!track.isLoading && track.residents.length === 0 && (
                  <div className="rounded-lg border border-dashed bg-background px-4 py-8 text-center">
                    <UserRound className="mx-auto mb-3 h-8 w-8 text-muted-foreground/50" />
                    <p className="text-sm font-medium">No residents yet</p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Residents are created when concerns include contact info, or when staff add them
                      manually.
                    </p>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="mt-4"
                      onClick={() => setCreateOpen(true)}
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      Add resident
                    </Button>
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>
        }
        detail={
          <ResidentDetail
            municipalityId={municipalityId}
            resident={track.selected}
            loading={track.isDetailLoading && !track.selected}
            updating={track.updating}
            onUpdate={(patch) => track.updateResident(patch)}
            onOpenComplaint={onOpenComplaint}
            onRefresh={() => track.refresh()}
          />
        }
      />
      <ResidentCreateDialog
        open={createOpen}
        onOpenChange={(open) => {
          setCreateOpen(open);
          if (!open) resetCreateForm();
        }}
        displayName={displayName}
        onDisplayNameChange={setDisplayName}
        email={email}
        onEmailChange={setEmail}
        phone={phone}
        onPhoneChange={setPhone}
        addressLine1={addressLine1}
        onAddressLine1Change={setAddressLine1}
        city={city}
        onCityChange={setCity}
        state={state}
        onStateChange={setState}
        postalCode={postalCode}
        onPostalCodeChange={setPostalCode}
        saving={track.creating}
        onSubmit={() => {
          void handleCreateResident();
        }}
      />
    </>
  );
}
